import { z } from '../validation/zod';
import type { Matrix } from '../geometry/matrix';
import {
  COLOR_PATTERN,
  CURRENT_SCHEMA_VERSION,
  DOCUMENT_LIMITS as L,
  FINGER_IDS,
  HANDS,
  ID_PATTERN,
  NAIL_SHAPE_IDS,
  type CatalogSource,
  type Design,
  type FillStyle,
  type NailInstance,
  type PathNode,
  type ProjectDocument,
  type StrokeStyle,
  type VectorNode,
} from './types';

/**
 * Runtime structural validation mirroring contracts/project.schema.json (strict
 * objects, identical patterns and limits). Semantic checks — references, unique IDs,
 * nonsingular matrices, path grammar, depth — live in validate.ts (docs/09).
 */
export const idSchema = z.string().regex(ID_PATTERN, 'Invalid identifier');
const nameSchema = z.string().min(1).max(L.maxNameLength);
const finiteNumber = z.number().refine(Number.isFinite, 'Must be a finite number');
export const matrixSchema: z.ZodType<Matrix> = z.tuple([
  finiteNumber,
  finiteNumber,
  finiteNumber,
  finiteNumber,
  finiteNumber,
  finiteNumber,
]);
export const colorSchema = z.string().regex(COLOR_PATTERN, 'Color must be #RRGGBB');

export const catalogSourceSchema: z.ZodType<CatalogSource> = z.strictObject({
  catalogId: idSchema,
  version: z.string().min(1).max(200),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
});

export const strokeSchema: z.ZodType<StrokeStyle> = z.strictObject({
  color: colorSchema,
  widthMm: finiteNumber.pipe(z.number().gt(0).max(L.maxStrokeWidthMm)),
  opacity: finiteNumber.pipe(z.number().min(0).max(1)),
  lineCap: z.enum(['round', 'butt', 'square']),
  lineJoin: z.enum(['round', 'miter', 'bevel']),
  scaleMode: z.enum(['fixed-mm', 'scale-with-design']),
});

export const fillSchema: z.ZodType<FillStyle> = z.strictObject({
  color: colorSchema,
  opacity: finiteNumber.pipe(z.number().min(0).max(1)),
  rule: z.enum(['nonzero', 'evenodd']),
});

const nodeBaseShape = {
  id: idSchema,
  name: nameSchema,
  transform: matrixSchema,
  visible: z.boolean(),
  locked: z.boolean(),
  source: catalogSourceSchema.exactOptional(),
};

export const pathNodeSchema: z.ZodType<PathNode> = z
  .strictObject({
    ...nodeBaseShape,
    type: z.literal('path'),
    d: z.string().min(1).max(L.maxPathDataLength),
    fill: z.union([z.literal('none'), fillSchema]),
    stroke: z.union([z.literal('none'), strokeSchema]),
  })
  .refine((node) => node.fill !== 'none' || node.stroke !== 'none', {
    message: 'A path needs a fill, a line, or both.',
    path: ['stroke'],
  });

export const vectorNodeSchema: z.ZodType<VectorNode> = z.lazy(() =>
  z.union([pathNodeSchema, groupNodeSchema]),
);

const groupNodeSchema = z.strictObject({
  ...nodeBaseShape,
  type: z.literal('group'),
  children: z.array(vectorNodeSchema).max(L.maxNodesPerContainer),
});

export const artboardSchema = z.strictObject({
  widthMm: finiteNumber.pipe(z.number().gt(0).max(L.maxArtboardMm)),
  heightMm: finiteNumber.pipe(z.number().gt(0).max(L.maxArtboardMm)),
});

export const designSchema: z.ZodType<Design> = z.strictObject({
  id: idSchema,
  name: nameSchema,
  artboard: artboardSchema,
  nodes: z.array(vectorNodeSchema).max(L.maxNodesPerContainer),
});

export const nailShapeSchema = z.strictObject({
  id: z.enum([...NAIL_SHAPE_IDS, 'custom']),
  version: z.string().min(1).max(200),
  normalizedPathD: z.string().min(1).max(50_000),
});

export const nailSchema: z.ZodType<NailInstance> = z.strictObject({
  id: idSchema,
  hand: z.enum(HANDS),
  finger: z.enum(FINGER_IDS),
  designId: idSchema,
  shape: nailShapeSchema,
  widthMm: finiteNumber.pipe(z.number().min(L.minNailWidthMm).max(L.maxNailWidthMm)),
  lengthMm: finiteNumber.pipe(z.number().min(L.minNailLengthMm).max(L.maxNailLengthMm)),
  placement: matrixSchema,
  baseColor: colorSchema,
  finish: z.enum(['matte', 'gloss']),
});

export const projectSchema: z.ZodType<ProjectDocument> = z.strictObject({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: idSchema,
  name: nameSchema,
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  revision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  designs: z.array(designSchema).min(1).max(L.maxDesigns),
  nails: z.array(nailSchema).max(L.maxNails),
  notes: z.string().max(L.maxNotesLength).exactOptional(),
});
